/**
 * RectangleTool: Interactive tool for drawing rectangle shapes
 * Feature: 049-critique-photo-markup
 *
 * Click-drag interaction: Top-left corner on mouse down, bottom-right on mouse up
 */

import { useState, useRef, useCallback } from 'react';
import type { RectangleShape, ShapeStyle, MarkupShape } from '@/types/markup.types';
import { pixelToPercentage, transformShapeForViewBox } from '@/utils/markupRenderer';
import { validateRectangle, isAccidentalClick } from '@/utils/markupValidation';
import { useMarkupDrag } from '@/hooks/useMarkupDrag';
import logger from '@/utils/logger';

interface RectangleToolProps {
  currentStyle: ShapeStyle;
  onShapeComplete: (shape: RectangleShape) => void;
  onValidationError: (message: string) => void;
  onToolClick: (x: number, y: number) => number | null;
  onShapeClick: (shapeNumber: number) => void;
  selectedShapeId: number | null;
  shapes: MarkupShape[];
  onShapeMove: (shapeNumber: number, shape: MarkupShape) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  photoAspectRatio?: number;
}

export function RectangleTool({
  currentStyle,
  onShapeComplete,
  onValidationError,
  onToolClick: _onToolClick, // Not used - hook handles hit detection internally
  onShapeClick,
  selectedShapeId,
  shapes,
  onShapeMove,
  containerRef,
  photoAspectRatio = 1,
}: RectangleToolProps) {
  const viewBoxHeight = 100 / photoAspectRatio;
  const viewBox = `0 0 100 ${viewBoxHeight}`;
  const [isDrawing, setIsDrawing] = useState(false);
  const [previewShape, setPreviewShape] = useState<RectangleShape | null>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);

  // Use shared drag handler for existing shapes and handles
  const {
    cursor: dragCursor,
    handleMouseDown: dragMouseDown,
    handleMouseMove: dragMouseMove,
    handleMouseUp: dragMouseUp,
    handleMouseLeave: dragMouseLeave,
  } = useMarkupDrag({
    shapes,
    selectedShapeId, // Pass through so hook can detect handle clicks
    onShapeClick, // Pass through to hook for direct shape selection
    onShapeMove,
    containerRef,
    photoAspectRatio,
    readOnly: false,
  });

  // Use drag cursor with fallback to crosshair for drawing
  const cursor = dragCursor ?? 'crosshair';

  /**
   * Get relative coordinates within container (percentage)
   * Accounts for object-contain letterboxing/pillarboxing
   */
  const getRelativeCoords = useCallback(
    (e: React.MouseEvent): { x: number; y: number } => {
      if (!containerRef.current) return { x: 0, y: 0 };

      const rect = containerRef.current.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const containerAspectRatio = containerWidth / containerHeight;

      // Calculate actual photo dimensions within container (object-contain behavior)
      let photoWidth, photoHeight, offsetX, offsetY;

      if (photoAspectRatio > containerAspectRatio) {
        // Photo is wider - letterboxing on top/bottom
        photoWidth = containerWidth;
        photoHeight = containerWidth / photoAspectRatio;
        offsetX = 0;
        offsetY = (containerHeight - photoHeight) / 2;
      } else {
        // Photo is taller - pillarboxing on left/right
        photoHeight = containerHeight;
        photoWidth = containerHeight * photoAspectRatio;
        offsetX = (containerWidth - photoWidth) / 2;
        offsetY = 0;
      }

      // Convert mouse position to photo-relative coordinates
      const pixelX = e.clientX - rect.left - offsetX;
      const pixelY = e.clientY - rect.top - offsetY;

      // Convert to percentage (0-100 for X, 0-viewBoxHeight for Y)
      return pixelToPercentage(pixelX, pixelY, photoWidth, photoHeight, photoAspectRatio);
    },
    [containerRef, photoAspectRatio]
  );

  /**
   * Calculate rectangle geometry from start and end points
   */
  const calculateRectangle = useCallback(
    (start: { x: number; y: number }, end: { x: number; y: number }) => {
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);
      const width = Math.abs(end.x - start.x);
      const height = Math.abs(end.y - start.y);

      return { x, y, width, height };
    },
    []
  );

  /**
   * Handle mouse down - check for existing shape/handle first, then start drawing
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // First, check if clicking on existing shape or handle - let shared handler manage it
      const handledByDrag = dragMouseDown(e);
      if (handledByDrag) {
        logger.debug('RectangleTool', 'Click handled by drag system');
        return;
      }

      // Not clicking on shape/handle - start drawing new rectangle
      const start = getRelativeCoords(e);
      startPointRef.current = start;
      setIsDrawing(true);

      // Create preview shape with zero dimensions
      const preview: RectangleShape = {
        type: 'rectangle',
        number: 0, // Will be assigned when added to shapes array
        strokeWidth: currentStyle.strokeWidth,
        strokeColor: currentStyle.strokeColor,
        strokeDasharray: currentStyle.strokeDasharray,
        geometry: {
          x: start.x,
          y: start.y,
          width: 0,
          height: 0,
        },
      };

      setPreviewShape(preview);
      logger.debug('RectangleTool', 'Started drawing rectangle at', start);
    },
    [dragMouseDown, getRelativeCoords, currentStyle]
  );

  /**
   * Handle mouse move - update preview rectangle or delegate to drag handler
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Delegate to drag handler (handles shape/handle dragging)
      dragMouseMove(e);

      // Handle drawing new shape
      if (!isDrawing || !startPointRef.current || !previewShape) return;

      const current = getRelativeCoords(e);
      const geometry = calculateRectangle(startPointRef.current, current);

      setPreviewShape((prev) =>
        prev
          ? {
              ...prev,
              geometry,
            }
          : null
      );
    },
    [isDrawing, previewShape, dragMouseMove, getRelativeCoords, calculateRectangle]
  );

  /**
   * Handle mouse up - finalize rectangle or complete drag
   */
  const handleMouseUp = useCallback(() => {
    // Delegate to drag handler (completes any active drag)
    dragMouseUp();

    // Complete drawing new shape
    if (!previewShape) return;

    // Check if this was just a click (deselect any selected shape)
    if (isAccidentalClick(previewShape)) {
      logger.debug('RectangleTool', 'Click detected - deselecting');
      onValidationError('click'); // Triggers deselection in editor
      setIsDrawing(false);
      setPreviewShape(null);
      startPointRef.current = null;
      return;
    }

    // Validate rectangle meets minimum size (pass viewBoxHeight for aspect-ratio-adjusted validation)
    const validation = validateRectangle(previewShape.geometry, viewBoxHeight);
    if (!validation.valid) {
      logger.warn('RectangleTool', 'Rectangle validation failed:', validation.error);
      onValidationError(validation.error || 'Rectangle too small, try again');
    } else {
      logger.info('RectangleTool', 'Rectangle created:', previewShape.geometry);
      onShapeComplete(previewShape);
    }

    // Reset state
    setIsDrawing(false);
    setPreviewShape(null);
    startPointRef.current = null;
  }, [previewShape, dragMouseUp, onShapeComplete, onValidationError, viewBoxHeight]);

  /**
   * Handle mouse leave - cancel drawing and clear hover state
   */
  const handleMouseLeaveWrapper = useCallback(() => {
    handleMouseUp(); // Cancel drawing if active
    dragMouseLeave(); // Clear hover state
  }, [handleMouseUp, dragMouseLeave]);

  return (
    <>
      {/* Transparent overlay to capture mouse events */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor,
          zIndex: 15,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeaveWrapper} // Cancel drawing and clear hover state
      />

      {/* Preview rectangle while drawing */}
      {previewShape && previewShape.geometry.width > 0 && previewShape.geometry.height > 0 && (() => {
        // BUG FIX (#480): Transform preview shape from image space to viewBox space
        const transformedPreview = transformShapeForViewBox(previewShape, photoAspectRatio);

        return (
          <svg
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 16,
            }}
          >
            <rect
              x={transformedPreview.geometry.x}
              y={transformedPreview.geometry.y}
              width={transformedPreview.geometry.width}
              height={transformedPreview.geometry.height}
              stroke={transformedPreview.strokeColor}
              strokeWidth={transformedPreview.strokeWidth}
              strokeDasharray={transformedPreview.strokeDasharray}
              fill="none"
              vectorEffect="non-scaling-stroke"
              opacity={0.5}
            />
          </svg>
        );
      })()}
    </>
  );
}
